import { JSONConfig } from '@/models/device.models';
import JSONEditor, { JSONEditorMode, JSONEditorOptions } from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';
import { isEmpty, isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

type JSONEditorFormItemProps = {
  value: JSONConfig;
  options?: JSONEditorOptions;
  mode?: JSONEditorMode;
  onValueChange: (value: JSONConfig) => void;
  onSetError: () => void;
};

export function JSONEditorFormItem({
  value,
  options,
  mode,
  onValueChange,
  onSetError,
}: JSONEditorFormItemProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<JSONEditor | null>(null);
  const [initialValue, setInitialValue] = useState<JSONConfig>(null);
  useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    editorRef.current.set(value ?? {});
    editorRef.current.setMode(mode!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Initialize editor once
  useEffect(() => {
    if (containerRef.current && !editorRef.current) {
      editorRef.current = new JSONEditor(containerRef.current, {
        mode: mode || 'view',
        navigationBar: false,
        mainMenuBar: false,
        search: false,
        ...options,
        onChange: () => {
          try {
            const parsed = editorRef.current?.get();
            // If the value passed in props is undefined and the current parsed value is also empty. It means no change
            if (initialValue === undefined && isEmpty(parsed)) {
              return;
            }
            onValueChange(parsed);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (_error) {
            onSetError();
          }
        },
      });
      editorRef.current.set(value);
    }

    return () => {
      if (editorRef.current) {
        // Set initial value
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
    // We want this to run only when the component is mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editorRef.current) {
      // value passed in props could be undefined, so we default to empty object
      const newValue = value ?? {};
      try {
        // Skip set() when the editor already holds this value (i.e. the change
        // originated from typing in the editor) — set() re-serializes the text
        // and would reformat/reset the caret on every keystroke
        if (!isEqual(editorRef.current.get(), newValue)) {
          editorRef.current.set(newValue);
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        // get() throws while the text is invalid JSON; fall back to set()
        editorRef.current.set(newValue);
      }
    }
    setInitialValue(value);
  }, [value]);

  return (
    <div
      ref={containerRef}
      className="jsoneditor-wrapper h-full w-full rounded-md"
    />
  );
}
